<template>
  <div ref="editorRef" class="supplement-editor" :class="{ 'is-disabled': isDisabled }">
    <SupplementName />

    <!-- Sidebar: Specific to Desktop, hidden on touch via CSS -->
    <div
      id="sidebar-options-column"
      class="supplement-editor-sidebar"
      :class="{
        'is-compact': sidebarCompact,
        'is-very-compact': sidebarVeryCompact,
        'is-ultra-compact': sidebarUltraCompact,
      }"
    >
      <SupplementSidebar @open-recipe-import="handleOpenRecipeImport" />
    </div>

    <!-- Main Content: Shared between Desktop and Mobile -->
    <div id="label-and-claim-div" class="supplement-editor-preview">
      <SupplementLabel />
    </div>

    <!-- Mobile-only panels: Teleported to body, hidden on desktop via CSS -->
    <Teleport to="body">
      <div class="no-print">
        <MobileSectionPanel
          :is-open="!!activeMobileSection"
          :title="activeMobileSectionTitle"
          :height-basis="activeMobileSectionHeightBasis"
          :section-key="activeMobileSection"
          :badge="activeMobileSectionCount"
          @close="activeMobileSection = null"
        >
          <template #header-actions>
            <ClearAllButton
              v-if="activeMobileSection === 'dv-lines'"
              :show="store.dvLines.length > 0"
              clear-type="dvLines"
              variant="mobile-header"
              :disabled="isClearing"
              @clear="requestClear"
            />
            <ClearAllButton
              v-else-if="activeMobileSection === 'lines'"
              :show="store.linesWithoutDv.length > 0"
              clear-type="lines"
              variant="mobile-header"
              :disabled="isClearing"
              @clear="requestClear"
            />
            <ClearAllButton
              v-else-if="activeMobileSection === 'dietary-info'"
              :show="filledDietaryLinesCount > 0"
              clear-type="dietary"
              variant="mobile-header"
              :disabled="isClearing"
              @clear="requestClear"
            />
            <ClearAllButton
              v-else-if="activeMobileSection === 'blends'"
              :show="store.blends.length > 0"
              clear-type="blends"
              variant="mobile-header"
              :disabled="isClearing"
              @clear="requestClear"
            />
          </template>
          <ServingSection v-if="activeMobileSection === 'serving'" />
          <DietaryInformationSection
            v-if="activeMobileSection === 'dietary-info'"
            @open-recipe-import="handleOpenRecipeImport"
          />
          <IngredientsWithDvSection v-if="activeMobileSection === 'dv-lines'" />
          <IngredientsWithoutDvSection v-if="activeMobileSection === 'lines'" />
          <BlendsSection v-if="activeMobileSection === 'blends'" />
          <SettingsSection v-if="activeMobileSection === 'label-settings'" />
        </MobileSectionPanel>
      </div>
    </Teleport>

    <!-- Mobile Bottom Bar: Teleported to body, hidden on desktop via CSS -->
    <Teleport to="body">
      <div class="supplement-editor-mobile-bottom no-print">
        <MobileBottomBar
          :active-section="activeMobileSection"
          @select="handleMobileSectionSelect"
        />
      </div>
    </Teleport>

    <ImportRecipeModal
      v-if="showImportModal"
      :user-id="store.userId"
      :label-id="store.id"
      :initial-recipe-id="pendingImportRecipeId"
      @close="handleImportModalClose"
      @imported="handleImported"
    />

    <ConfirmDialog
      v-model="showClearConfirm"
      :title="confirmConfig?.title || ''"
      :message="confirmConfig?.message || ''"
      :confirm-text="confirmConfig?.confirmText || 'Confirm'"
      :is-loading="isClearing"
      @confirm="performClear"
      @cancel="cancelClear"
    />

    <div class="sr-only" aria-live="polite" aria-atomic="true" data-test-id="status-announcer">
      {{ statusAnnouncement }}
    </div>
  </div>
</template>

<script setup>
  import { onMounted, onUnmounted, nextTick, watch, computed, ref, provide } from "vue";

  const editorRef = ref(null);
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import { useAutoSave, isSaveInFlight } from "./composables/use-auto-save";
  import { useVersionPolling } from "./composables/use-version-polling";
  import { useSupplementClearOperations } from "./composables/use-supplement-clear-operations";
  import { bindPreviewButton } from "~/label/preview_button";
  import { initializeSupplementLogoModal } from "~/label/logo";
  import { initializeSupplementBarcodeModal } from "~/label/barcode";
  import { Spinner } from "~/jose_modern/spinner";
  import { initStickySubnavHeader } from "~/jose_modern/sticky_subnav_header";
  import SupplementSidebar from "../supplement-sidebar.vue";
  import SupplementLabel from "../supplement-label.vue";
  import MobileBottomBar from "./mobile-bottom-bar.vue";
  import MobileSectionPanel from "./mobile-section-panel.vue";
  import ServingSection from "./serving-section.vue";
  import DietaryInformationSection from "./dietary-information-section.vue";
  import IngredientsWithDvSection from "./ingredients-with-dv-section.vue";
  import IngredientsWithoutDvSection from "./ingredients-without-dv-section.vue";
  import BlendsSection from "./blends-section.vue";
  import SettingsSection from "./settings-section.vue";
  import ImportRecipeModal from "./import-recipe-modal.vue";
  import ConfirmDialog from "../shared/confirm-dialog.vue";
  import ClearAllButton from "./clear-all-button.vue";
  import SupplementName from "./supplement-name.vue";
  import useViewLoadTracking from "../lib/use-view-load-tracking.js";

  useViewLoadTracking();

  const props = defineProps({
    initialState: { type: Object, required: true },
    supplementId: { type: [String, Number], required: true },
    importRecipeId: { type: [String, Number], default: null },
  });

  const store = useSupplementLabelStore();

  const showImportModal = ref(false);
  const pendingImportRecipeId = ref(props.importRecipeId ? Number(props.importRecipeId) : null);
  const statusAnnouncement = ref("");

  function clearImportRecipeIdParam() {
    if (pendingImportRecipeId.value) {
      pendingImportRecipeId.value = null;
      const url = new URL(window.location.href);
      url.searchParams.delete("import_recipe_id");
      window.history.replaceState({}, "", url.toString());
    }
  }

  function handleImported(editorState) {
    showImportModal.value = false;
    clearImportRecipeIdParam();
    store.initialize(editorState);
  }

  function handleImportModalClose() {
    showImportModal.value = false;
    clearImportRecipeIdParam();
  }

  function handleOpenRecipeImport() {
    store.debugLog("SupplementEditor: open-recipe-import event received");
    showImportModal.value = true;
  }

  // Initialize store synchronously so isPublic is available before child onMounted hooks
  store.initialize(props.initialState);

  // Call composables at setup level so lifecycle hooks work
  const { save, retry } = useAutoSave(store, props.supplementId);
  useVersionPolling(store, props.supplementId);

  // Provide triggerSave for section components used in mobile panel
  provide("triggerSave", save);

  // Clear operations composable
  const { showClearConfirm, confirmConfig, isClearing, requestClear, cancelClear, performClear } =
    useSupplementClearOperations(store, save);

  provide("requestClear", requestClear);

  const isDisabled = computed(() => store.isConflicted || store.isReloading);

  function handleGlobalChange(event) {
    // Save on commit-only events (native change)
    const target = event.target;
    if (target) {
      save();
    }
  }

  function handleLogoUpdated(event) {
    const data = event.detail;
    if (!data) return;

    // Update logo-related fields
    store.logoImage = data.logo || null;
    store.logoScale = parseFloat(data.logo_scale || store.logoScale || 1.0);
    store.logoMargin = parseFloat(data.logo_margin || store.logoMargin || 8);
    store.hideLogo = Boolean(data.hide_logo);

    // Critical: Update lock_version to prevent stale update conflicts
    if (typeof data.lock_version === "number") {
      store.updateLockVersion(data.lock_version);
    }
  }

  function handleBarcodeUpdated(event) {
    const data = event.detail;
    if (!data) return;
    store.primaryBarcode = data.barcode || null;
    if (typeof data.hide_barcode === "boolean") {
      store.hideBarcode = data.hide_barcode;
    }
    if (typeof data.lock_version === "number") {
      store.updateLockVersion(data.lock_version);
    }
  }

  function handleOpenLogoModal() {
    if (window.Modal) {
      window.Modal.show("#logo-modal");
      nextTick(() => initializeSupplementLogoModal());
    }
  }

  function handleOpenBarcodeModal() {
    if (window.Modal) {
      window.Modal.show("#barcode-modal");
      nextTick(() => initializeSupplementBarcodeModal());
    }
  }

  function handleLabelActionsAltClick() {
    if (window.Modal) {
      window.Modal.show("#label-actions-modal");
    }
  }

  function handleReloadRequest() {
    if (store.isSaving || store.isCalculating) return;
    store.reloadFromServer();
  }

  function setupLabelActionsSelectListeners() {
    document.querySelectorAll(".label-actions").forEach((container) => {
      const renderSelect = container.querySelector(".js-render-select");
      const downloadForms = container.querySelectorAll(".js-download-form");

      if (!renderSelect || downloadForms.length === 0) return;

      const updateVisibility = () => {
        const selectedValue = renderSelect.value;
        downloadForms.forEach((form) => {
          const formName = form.getAttribute("data-form-name");
          form.classList.toggle("is-hidden", formName !== selectedValue);
        });
      };

      renderSelect.addEventListener("change", updateVisibility);
      updateVisibility();
    });
  }

  function updateLabelActionsDimensions(dimensionsText) {
    const content = dimensionsText
      ? `<div>Label dimensions:</div><div class="no-wrap">${dimensionsText}</div>`
      : "";
    document.querySelectorAll(".download-preview-dimensions .dimensions-content").forEach((el) => {
      el.innerHTML = content;
    });
  }

  function syncWindowLabelData() {
    window.supplement_label = {
      id: store.id,
      logo: store.logoImage,
      logo_scale: store.logoScale,
      logo_margin: store.logoMargin,
      hide_logo: store.hideLogo,
      primary_barcode: store.primaryBarcode,
      hide_barcode: store.hideBarcode,
    };
  }

  const filledDietaryLinesCount = computed(() => {
    return store.dietaryLines.filter((l) => l.amount).length;
  });

  let stopLabelDataWatchers = [];

  const sidebarCompact = ref(false);
  const sidebarVeryCompact = ref(false);
  const sidebarUltraCompact = ref(false);
  let sidebarCompactTimeout = null;
  let cleanupStickyHeader = null;

  // Mobile section panel state
  const activeMobileSection = ref(null);

  const mobileSectionTitles = {
    "serving": "Serving Information",
    "dietary-info": "Dietary Information",
    "dv-lines": "Vitamins & Minerals",
    "lines": "Ingredients without DVs",
    "blends": "Blends",
    "label-settings": "Label Settings",
  };

  const mobileSectionHeightBasis = {
    "serving": 35,
    "dietary-info": 60,
    "dv-lines": 55,
    "lines": 55,
    "blends": 73,
    "label-settings": 32,
  };

  const defaultMobileSectionHeightBasis = 50;

  const activeMobileSectionTitle = computed(() => {
    return mobileSectionTitles[activeMobileSection.value] || "";
  });

  const activeMobileSectionHeightBasis = computed(() => {
    return mobileSectionHeightBasis[activeMobileSection.value] || defaultMobileSectionHeightBasis;
  });

  const activeMobileSectionCount = computed(() => {
    const section = activeMobileSection.value;
    if (section === "dv-lines") return store.dvLines.length || null;
    if (section === "lines") return store.linesWithoutDv.length || null;
    if (section === "blends") return store.blends.length || null;
    return null;
  });

  function handleMobileSectionSelect(sectionKey) {
    activeMobileSection.value = sectionKey;
  }

  function handleOpenFacilityAllergensPrefix() {
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      activeMobileSection.value = "label-settings";
    }
  }

  function handleOpenSectionRequest(event) {
    const isMobile = window.innerWidth < 1024;
    if (isMobile && event.detail?.key) {
      activeMobileSection.value = event.detail.key;
    }
  }

  function handleBeforeUnload(event) {
    if (isSaveInFlight() || store.error) {
      event.preventDefault();
      return "";
    }
  }

  onMounted(() => {
    const wrapper = document.getElementById("supplement-editor-wrapper");
    if (wrapper) {
      Spinner.stop(wrapper);
    }

    window.supplementLabelStore = store;

    store.registerRetryCallback(retry);

    // Set up native change event listener for autosave
    // Using addEventListener instead of Vue's @change because Vue's @change on root element
    // doesn't properly catch bubbled events from child elements
    if (editorRef.value) {
      editorRef.value.addEventListener("change", handleGlobalChange);
    }

    bindPreviewButton();

    window.addEventListener("supplement:logo-updated", handleLogoUpdated);
    window.addEventListener("supplement:barcode-updated", handleBarcodeUpdated);
    window.addEventListener("supplement:open-logo-modal", handleOpenLogoModal);
    window.addEventListener("supplement:open-barcode-modal", handleOpenBarcodeModal);
    window.addEventListener("supplement:request-reload", handleReloadRequest);
    window.addEventListener(
      "supplement:open-facility-allergens-prefix",
      handleOpenFacilityAllergensPrefix
    );
    window.addEventListener("supplement:open-section", handleOpenSectionRequest);
    window.addEventListener("beforeunload", handleBeforeUnload);

    watch(() => store.renderedDimensionsText, updateLabelActionsDimensions, { immediate: true });

    syncWindowLabelData();
    stopLabelDataWatchers = [
      watch(() => store.logoImage, syncWindowLabelData),
      watch(() => store.logoScale, syncWindowLabelData),
      watch(() => store.logoMargin, syncWindowLabelData),
      watch(() => store.hideLogo, syncWindowLabelData),
      watch(() => store.primaryBarcode, syncWindowLabelData),
      watch(() => store.hideBarcode, syncWindowLabelData),
      watch(
        () => store.name,
        (newName) => {
          if (newName) {
            document.title = newName;
          }
        },
        { immediate: true }
      ),
    ];

    document
      .getElementById("download-button")
      ?.addEventListener("click", handleLabelActionsAltClick);

    setupLabelActionsSelectListeners();

    const updateSidebarCompact = () => {
      const labelWidth = store.effectiveLabelWidth;
      sidebarCompact.value = labelWidth > 500;
      sidebarVeryCompact.value = labelWidth > 600;
      sidebarUltraCompact.value = labelWidth > 700;
    };

    const debouncedUpdateSidebarCompact = () => {
      clearTimeout(sidebarCompactTimeout);
      sidebarCompactTimeout = setTimeout(updateSidebarCompact, 150);
    };

    updateSidebarCompact();
    watch(() => store.effectiveLabelWidth, debouncedUpdateSidebarCompact);

    cleanupStickyHeader = initStickySubnavHeader();

    // Auto-open import modal if arriving from recipe#show "FDA Supplement" dropdown
    if (pendingImportRecipeId.value) {
      showImportModal.value = true;
    }
  });

  onUnmounted(() => {
    // Remove native change listener
    if (editorRef.value) {
      editorRef.value.removeEventListener("change", handleGlobalChange);
    }

    window.removeEventListener("supplement:logo-updated", handleLogoUpdated);
    window.removeEventListener("supplement:barcode-updated", handleBarcodeUpdated);
    window.removeEventListener("supplement:open-logo-modal", handleOpenLogoModal);
    window.removeEventListener("supplement:open-barcode-modal", handleOpenBarcodeModal);
    window.removeEventListener("supplement:request-reload", handleReloadRequest);
    window.removeEventListener(
      "supplement:open-facility-allergens-prefix",
      handleOpenFacilityAllergensPrefix
    );
    window.removeEventListener("supplement:open-section", handleOpenSectionRequest);
    window.removeEventListener("beforeunload", handleBeforeUnload);
    stopLabelDataWatchers.forEach((stop) => stop());
    clearTimeout(sidebarCompactTimeout);
    if (cleanupStickyHeader) {
      cleanupStickyHeader();
      cleanupStickyHeader = null;
    }
    store.unregisterRetryCallback();
    delete window.supplement_label;
    delete window.supplementLabelStore;
  });
</script>

<style lang="scss">
  @import "~/label/css/labels/supplements/supplement_editor";
</style>

<style lang="scss" scoped>
  :deep(.button.is-small) {
    min-height: 2rem; // 32px
  }

  :deep(.mobile-header-action) {
    border: none;
    box-shadow: none;
    height: 2rem;
    font-size: 1rem;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
